import { fromSubdomainRegex } from './subdomainRegex.helper';

import type { AppSitePublishersConfiguration, AppSiteSearchEntry } from '@/contexts/Targeting/TargetingContext';
import type {
  AppSiteMarketplaceType,
  InventoryOperation,
  InventoryRow,
  StrictExportRow
} from '../../AppAndSiteMarketplace.types';

type RowsConfig = Pick<
  AppSitePublishersConfiguration,
  | 'blacklistedGenericPublishers'
  | 'blacklistedPublishers'
  | 'blacklistedStrictPublishers'
  | 'blacklistedStrictSearches'
  | 'whitelistedGenericPublishers'
  | 'whitelistedPublishers'
  | 'whitelistedStrictPublishers'
  | 'whitelistedStrictSearches'
>;

type StrictList = {
  publishers?: string[];
  searches?: AppSiteSearchEntry[];
};

type StrictListResult = {
  publishers: string[];
  searches: AppSiteSearchEntry[];
};

const uniq = (items: string[]): string[] => [...new Set(items)];

/** Entries intersected with the list's publishers; entries left without keys are dropped. */
export const reconcileEntries = (
  searches: AppSiteSearchEntry[] = [],
  publishers: string[] = []
): AppSiteSearchEntry[] => {
  const publishersSet = new Set(publishers);
  const bySearch = new Map<string, string[]>();
  for (const { keys, search } of searches) {
    const kept = keys.filter(key => publishersSet.has(key));
    bySearch.set(search, uniq([...(bySearch.get(search) ?? []), ...kept]));
  }
  return [...bySearch.entries()].filter(([, keys]) => keys.length > 0).map(([search, keys]) => ({ keys, search }));
};

/** Rows of a strict list: one per (reconciled) search entry plus one per legacy key not covered by any entry. */
export const buildStrictRows = ({ publishers = [], searches = [] }: StrictList): InventoryRow[] => {
  const entries = reconcileEntries(searches, publishers);
  const covered = new Set(entries.flatMap(entry => entry.keys));
  const entryIds = new Set(entries.map(entry => entry.search));
  const entryRows: InventoryRow[] = entries.map(({ keys, search }) => ({
    id: search,
    isEntry: true,
    keys,
    label: search
  }));
  // A legacy key named like an entry is not rendered again: the entry row prevails.
  const legacyRows: InventoryRow[] = uniq(publishers)
    .filter(key => !covered.has(key) && !entryIds.has(key))
    .map(key => ({ id: key, isEntry: false, keys: [key], label: key }));
  return [...entryRows, ...legacyRows];
};

/** Rows of plain (generic) lists, one per distinct stored value and without tag. Stored subdomain regexes show their hostname. */
export const buildPlainRows = (...lists: (string[] | undefined)[]): InventoryRow[] =>
  uniq(lists.flatMap(items => items ?? [])).map(item => ({
    id: item,
    isEntry: false,
    keys: [item],
    label: fromSubdomainRegex(item) ?? item
  }));

/** Rows of the included/excluded list of a tab. Only the `specific` tab has search entries; `generic` mixes domains and subdomains. */
export const getInventoryRows = (
  config: RowsConfig | undefined,
  type: AppSiteMarketplaceType,
  operation: InventoryOperation
): InventoryRow[] => {
  const { include, exclude } =
    type === 'specific'
      ? {
          exclude: buildStrictRows({
            publishers: config?.blacklistedStrictPublishers,
            searches: config?.blacklistedStrictSearches
          }),
          include: buildStrictRows({
            publishers: config?.whitelistedStrictPublishers,
            searches: config?.whitelistedStrictSearches
          })
        }
      : {
          exclude: buildPlainRows(config?.blacklistedGenericPublishers, config?.blacklistedPublishers),
          include: buildPlainRows(config?.whitelistedGenericPublishers, config?.whitelistedPublishers)
        };
  return operation === 'include' ? include : exclude;
};

/** Merges new entries into a strict list: same `search` unions the keys (keeping order), publishers get the new keys. */
export const mergeStrictEntries = (
  { publishers = [], searches = [] }: StrictList,
  entries: AppSiteSearchEntry[]
): StrictListResult => {
  const found = entries.filter(entry => entry.keys.length > 0);
  const nextPublishers = uniq([...publishers, ...found.flatMap(entry => entry.keys)]);
  return {
    publishers: nextPublishers,
    searches: reconcileEntries([...reconcileEntries(searches, publishers), ...found], nextPublishers)
  };
};

/** Removes rows (by id) of a strict list. Keys are only removed when no remaining entry of the list covers them. */
export const deleteStrictRows = (list: StrictList, ids: ReadonlySet<string>): StrictListResult => {
  const { publishers = [], searches = [] } = list;
  const rows = buildStrictRows(list);
  const removed = rows.filter(row => ids.has(row.id));
  const removedEntryIds = new Set(removed.filter(row => row.isEntry).map(row => row.id));
  const remainingEntries = reconcileEntries(searches, publishers).filter(entry => !removedEntryIds.has(entry.search));
  const stillCovered = new Set(remainingEntries.flatMap(entry => entry.keys));
  const keysToRemove = new Set(removed.flatMap(row => row.keys).filter(key => !stillCovered.has(key)));
  return {
    publishers: publishers.filter(key => !keysToRemove.has(key)),
    searches: remainingEntries
  };
};

/** Ids of the `rows` with a key that is also in `otherRows` (by key, not by row). */
export const getConflictingRowIds = (rows: InventoryRow[], otherRows: InventoryRow[]): Set<string> => {
  const otherKeys = new Set(otherRows.flatMap(row => row.keys));
  return new Set(rows.filter(row => row.keys.some(key => otherKeys.has(key))).map(row => row.id));
};

/** Builds entries from import rows (`searchString` + `keys`) of one kind (`whitelisting`/`blacklisting`). */
export const getImportEntries = (
  items: { keys?: string[]; searchString?: string; type?: string }[],
  type: 'blacklisting' | 'whitelisting'
): AppSiteSearchEntry[] =>
  items
    .filter(item => item.type === type)
    .map(item => ({ keys: (item.keys ?? []).filter(Boolean), search: item.searchString ?? '' }))
    .filter(entry => entry.search !== '' && entry.keys.length > 0);

/** One export row per key: entries repeat their search, legacy keys have an empty search. */
export const getStrictExportRows = (rows: InventoryRow[]): StrictExportRow[] =>
  rows.flatMap(row => row.keys.map(key => ({ key, search: row.isEntry ? row.label : '' })));

/** A key in both lists is exported only in `exclude` (exclusions take priority). */
export const resolveStrictExportRows = ({
  exclude,
  include
}: {
  exclude: StrictExportRow[];
  include: StrictExportRow[];
}): { exclude: StrictExportRow[]; include: StrictExportRow[] } => {
  const excludedKeys = new Set(exclude.map(row => row.key));
  return { exclude, include: include.filter(row => !excludedKeys.has(row.key)) };
};

/** Config without the empty `*StrictSearches`, so a backend `[]` default doesn't count as "configured". */
export const omitEmptyStrictSearches = <T extends AppSitePublishersConfiguration>(config: T): T => {
  const next = { ...config };
  if (!next.whitelistedStrictSearches?.length) {
    delete next.whitelistedStrictSearches;
  }
  if (!next.blacklistedStrictSearches?.length) {
    delete next.blacklistedStrictSearches;
  }
  return next;
};
